import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Crown } from "lucide-react";
import { POKER_GLOSSARY } from "../lib/constants";
import { useAuth } from "../lib/auth";

export const PlayingCard = ({ card, size = "md" }) => {
  if (!card) return null;
  const rank = card.slice(0, -1);
  const suit = card.slice(-1);
  const suitSymbols = { s: "\u2660", h: "\u2665", d: "\u2666", c: "\u2663" };
  const suitConfig = {
    h: { bg: "linear-gradient(145deg, #E8232A 0%, #B91C1C 100%)", text: "#fff" },
    d: { bg: "linear-gradient(145deg, #2563EB 0%, #1D4ED8 100%)", text: "#fff" },
    c: { bg: "linear-gradient(145deg, #059669 0%, #047857 100%)", text: "#fff" },
    s: { bg: "linear-gradient(145deg, #374151 0%, #1F2937 100%)", text: "#fff" }
  };
  const config = suitConfig[suit];
  const sizes = {
    sm:    { w: "36px", h: "48px", rank: "16px", suit: "12px", gap: "1px" },
    md:    { w: "48px", h: "64px", rank: "22px", suit: "16px", gap: "2px" },
    lg:    { w: "60px", h: "80px", rank: "28px", suit: "20px", gap: "2px" },
    xl:    { w: "68px", h: "92px", rank: "32px", suit: "22px", gap: "3px" },
    board: { w: "52px", h: "70px", rank: "24px", suit: "17px", gap: "2px" }
  };
  const s = sizes[size] || sizes.md;
  return (
    <div
      className="rounded-md flex flex-col items-center justify-center select-none relative overflow-hidden"
      style={{ width: s.w, height: s.h, background: config.bg, boxShadow: "0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)" }}
      data-testid={`playing-card-${card}`}
    >
      <div className="absolute inset-0 opacity-10" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%, transparent 100%)" }} />
      <span style={{ fontSize: s.rank, fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: config.text, lineHeight: 1, textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{rank}</span>
      <span style={{ fontSize: s.suit, color: config.text, lineHeight: 1, marginTop: s.gap, opacity: 0.85, filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.2))" }}>{suitSymbols[suit]}</span>
    </div>
  );
};

export const PositionNode = ({ label, stack, isHero = false, isVillain = false, cards = [], action, x, y }) => {
  const cleanLabel = label.replace(/\s*\(.*\)/, "").split("/")[0].trim();
  return (
    <div className="absolute flex flex-col items-center gap-1 transform -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%` }}>
      {action && (
        <div className={`px-2 py-0.5 rounded text-[9px] sm:text-[10px] font-semibold whitespace-nowrap mb-0.5 ${
          /fold/i.test(action) ? "bg-neutral-700/80 text-neutral-400" :
          /raise|bet|shove|3-bet|4-bet/i.test(action) ? "bg-red-500/20 text-red-400" :
          /call|check/i.test(action) ? "bg-teal-500/20 text-teal-400" :
          "bg-neutral-700/80 text-neutral-300"
        }`}>{action}</div>
      )}
      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex flex-col items-center justify-center text-xs font-bold border-2 ${
        isHero ? "border-teal-400 bg-teal-500/20 text-teal-300" :
        isVillain ? "border-orange-400 bg-orange-500/20 text-orange-300" :
        "border-neutral-600 bg-neutral-800 text-neutral-400"
      }`}>
        <span className="text-[10px] sm:text-xs uppercase truncate max-w-[40px] sm:max-w-[48px]">{cleanLabel}</span>
        {stack && <span className="text-[9px] sm:text-[10px] font-mono opacity-70">{stack}</span>}
      </div>
      {cards.length > 0 && (
        <div className="flex gap-0.5">
          {cards.map((c) => <PlayingCard key={c} card={c} size="sm" />)}
        </div>
      )}
    </div>
  );
};

export const GlossaryTag = ({ tag }) => {
  const [show, setShow] = useState(false);
  const key = tag.toLowerCase().replace(/[^a-z0-9\-/\s]/g, "").trim();
  const definition = POKER_GLOSSARY[key] || POKER_GLOSSARY[tag] || null;
  return (
    <span className="relative inline-block" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} onTouchStart={() => setShow(s => !s)}>
      <span className={`px-2 py-0.5 rounded text-xs cursor-help transition-colors ${
        definition ? "bg-white/5 text-neutral-300 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] border border-transparent hover:border-[#D4AF37]/20" : "bg-white/5 text-neutral-400"
      }`}>{tag}</span>
      {definition && show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 rounded-lg bg-[#1a1a1a] border border-white/10 shadow-xl text-xs text-neutral-300 leading-relaxed">
          <div className="font-semibold text-[#D4AF37] mb-1 capitalize">{tag}</div>
          <div>{definition}</div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1a1a1a] border-r border-b border-white/10 rotate-45 -mt-1" />
        </div>
      )}
    </span>
  );
};

export const AdBanner = ({ variant = "horizontal" }) => {
  const { user } = useAuth();
  if (user?.is_premium) return null;
  const ads = [
    { title: "Go Ad-Free", desc: "Remove ads for $10/mo or $70 lifetime", cta: "Remove Ads", link: "/profile" },
    { title: "Distraction-Free Training", desc: "Get a cleaner experience with no ads", cta: "Go Ad-Free", link: "/profile" },
    { title: "Lifetime Deal", desc: "One payment of $70, ad-free forever", cta: "$70 Lifetime", link: "/profile" }
  ];
  const ad = ads[Math.floor(Date.now() / 30000) % ads.length];
  if (variant === "sidebar") {
    return (
      <div className="bg-[#121212] border border-white/5 rounded-lg p-4 text-center" data-testid="ad-banner-sidebar">
        <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Sponsored</div>
        <div className="text-sm font-medium mb-1">{ad.title}</div>
        <div className="text-xs text-neutral-400 mb-3">{ad.desc}</div>
        <Link to={ad.link} className="inline-block bg-[#D4AF37] text-black text-xs font-semibold rounded-md px-4 py-2 hover:bg-[#B3922A] transition-colors">{ad.cta}</Link>
      </div>
    );
  }
  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="ad-banner">
      <div className="flex items-center gap-3">
        <div className="text-xs text-neutral-500 uppercase tracking-wider">Ad</div>
        <div className="text-sm"><span className="font-medium">{ad.title}</span><span className="text-neutral-400 ml-2 hidden sm:inline">{ad.desc}</span></div>
      </div>
      <Link to={ad.link} className="bg-[#D4AF37] text-black text-xs font-semibold rounded-md px-3 py-1.5 hover:bg-[#B3922A] transition-colors whitespace-nowrap">{ad.cta}</Link>
    </div>
  );
};

// ============ SITEWIDE AD TOGGLE ============
// Set to false to disable all ads across the entire app
const ADS_ENABLED = true;

// Google AdSense Ad Slot Component
export const AdSlot = ({ size = "banner", className = "" }) => {
  const { user } = useAuth();
  const adRef = useRef(null);
  const pushed = useRef(false);
  
  // Don't render if ads disabled sitewide or user is ad-free
  const showAd = ADS_ENABLED && !user?.is_premium;

  useEffect(() => {
    if (!showAd || pushed.current) return;
    try {
      if (adRef.current && window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      }
    } catch { /* adsbygoogle not loaded yet */ }
  }, [showAd]);

  if (!showAd) return null;

  return (
    <div 
      className={`w-full flex flex-col items-center my-4 ${className}`}
      data-testid={`ad-slot-${size}`}
    >
      <div className="w-full" style={{ maxWidth: size === "rectangle" ? "300px" : size === "mobile" ? "320px" : size === "large" ? "336px" : "728px" }}>
        <ins 
          ref={adRef}
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-2616923703813680"
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
      <Link
        to="/profile"
        className="mt-3 inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-semibold rounded-full px-4 py-1.5 hover:bg-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all"
        data-testid="go-ad-free-btn"
      >
        <Crown className="w-3.5 h-3.5" />
        Go Ad-Free
      </Link>
    </div>
  );
};

export const PokerTable = ({ scenario }) => {
  const { TABLE_POSITIONS, TABLE_COORDS } = require("../lib/constants");
  const { resolveHeroSeat, resolveVillainSeats, parsePositionActions } = require("../lib/constants");

  const heroRaw = scenario.positions?.hero || "";
  const heroSeat = resolveHeroSeat(heroRaw);
  const villainSeats = resolveVillainSeats(scenario.positions?.villain || "");
  const posActions = parsePositionActions(scenario.action_history, heroSeat);

  return (
    <div className="bg-[#1a1a1a] rounded-xl border border-white/5 relative overflow-hidden" style={{ minHeight: "380px" }}>
      <div className="absolute inset-8 sm:inset-12 rounded-[50%] border border-neutral-700/50" style={{ background: "radial-gradient(ellipse at center, #1e2a1e 0%, #141414 100%)" }} />
      {TABLE_POSITIONS.map((pos, idx) => {
        const isHero = pos === heroSeat;
        const isVillain = villainSeats.has(pos) && !isHero;
        return (
          <PositionNode
            key={pos}
            label={isHero ? heroRaw : pos}
            stack={isHero ? `${scenario.stack_sizes?.hero}` : isVillain ? `${scenario.stack_sizes?.villain}` : ""}
            isHero={isHero}
            isVillain={isVillain}
            cards={isHero ? scenario.hero_hand : []}
            action={posActions[pos] || null}
            x={TABLE_COORDS[idx].x}
            y={TABLE_COORDS[idx].y}
          />
        );
      })}
      <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
        <div className="text-xs text-neutral-400 font-mono mb-1">{scenario.blinds} | {scenario.stack_sizes?.hero}BB eff.</div>
        {scenario.board_cards?.length > 0 ? (
          <div className="flex gap-1">
            {scenario.board_cards.map((card) => (
              <div key={card} className="animate-card-slide"><PlayingCard card={card} size="board" /></div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-neutral-500 bg-neutral-800/50 px-3 py-1 rounded">PREFLOP</div>
        )}
      </div>
    </div>
  );
};
